import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  HelpCircle,
  UserRound,
  Stethoscope,
  CalendarCheck,
  Video,
  FileText,
  Pill,
  CreditCard,
  ShieldCheck,
  Settings,
  MessageSquare,
  Mail,
  ArrowRight,
  ChevronRight,
  BookOpen,
  Headphones,
  ExternalLink,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const categories = [
    {
      icon: UserRound,
      title: "Getting Started",
      description:
        "Learn the basics of Doxzon and get started with your healthcare journey.",
      articles: 8,
    },
    {
      icon: CalendarCheck,
      title: "Appointments",
      description:
        "Find help with booking, managing and cancelling appointments.",
      articles: 12,
    },
    {
      icon: Video,
      title: "Virtual Care",
      description:
        "Learn how to prepare for and join virtual healthcare consultations.",
      articles: 9,
    },
    {
      icon: FileText,
      title: "Health Records",
      description:
        "Understand how digital health records and documents are managed.",
      articles: 10,
    },
    {
      icon: Pill,
      title: "Prescriptions",
      description:
        "Get help with prescriptions and related healthcare workflows.",
      articles: 7,
    },
    {
      icon: CreditCard,
      title: "Billing & Payments",
      description:
        "Find answers about payments, billing and transaction-related questions.",
      articles: 11,
    },
    {
      icon: ShieldCheck,
      title: "Privacy & Security",
      description:
        "Learn about privacy, security and responsible handling of information.",
      articles: 6,
    },
    {
      icon: Settings,
      title: "Account & Settings",
      description:
        "Manage your account, preferences and general platform settings.",
      articles: 9,
    },
  ];

  const popularArticles = [
    {
      category: "Getting Started",
      title: "How do I create a Doxzon account?",
      description:
        "A quick guide to getting started with your Doxzon account.",
    },
    {
      category: "Appointments",
      title: "How can I book an appointment?",
      description:
        "Learn how to find a healthcare professional and schedule an appointment.",
    },
    {
      category: "Virtual Care",
      title: "How do I join a virtual consultation?",
      description:
        "Everything you need to know before joining a virtual care session.",
    },
    {
      category: "Health Records",
      title: "Where can I find my health records?",
      description:
        "Learn where digital health information and documents can be accessed.",
    },
    {
      category: "Account",
      title: "How can I update my account information?",
      description:
        "Manage your profile and update your account details.",
    },
    {
      category: "Privacy",
      title: "How does Doxzon protect my information?",
      description:
        "Learn about Doxzon's approach to privacy and information security.",
    },
  ];

  const quickLinks = [
    {
      icon: BookOpen,
      title: "Browse all articles",
      description: "Explore the complete Doxzon knowledge base.",
    },
    {
      icon: Headphones,
      title: "Contact support",
      description: "Can't find what you're looking for? Talk to our team.",
    },
    {
      icon: MessageSquare,
      title: "Send us feedback",
      description: "Tell us how we can make Doxzon better.",
    },
  ];

  const filteredCategories = categories.filter((category) =>
    category.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredArticles = popularArticles.filter(
    (article) =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO / SEARCH
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute -top-40 -right-40 w-[650px] h-[650px] rounded-full bg-teal-200/25 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-200/25 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 text-center">

            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Doxzon Help Center
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                How can we
                <span className="text-teal-500"> help?</span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-6 max-w-2xl mx-auto">
                Find answers, guides and helpful information about using
                Doxzon.
              </p>

              {/* SEARCH BOX */}
              <div className="max-w-3xl mx-auto mt-9">

                <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_15px_50px_rgba(15,23,42,0.1)] p-2 flex items-center">

                  <Search
                    size={21}
                    className="text-slate-400 ml-4 shrink-0"
                  />

                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for answers..."
                    className="flex-1 h-12 px-4 text-sm md:text-base outline-none bg-transparent"
                  />

                  <button className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-12 text-sm font-semibold hover:bg-teal-500 transition">

                    Search

                    <ArrowRight size={15} />

                  </button>

                </div>

                <p className="text-xs text-slate-400 mt-3">
                  Try searching for "appointments", "prescriptions" or
                  "virtual care"
                </p>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            CATEGORIES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Browse Help
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                What can we help you
                <span className="block text-teal-500">
                  with today?
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Choose a category to find guides and answers related to your
                Doxzon experience.
              </p>

            </div>

            {filteredCategories.length > 0 ? (

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">

                {filteredCategories.map((item, index) => {

                  const Icon = item.icon;

                  return (
                    <motion.button
                      key={item.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ y: -6 }}
                      className="text-left group rounded-3xl border border-slate-100 bg-slate-50/70 p-7 hover:bg-white hover:border-teal-100 hover:shadow-xl transition"
                    >

                      <div className="flex items-start justify-between">

                        <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">

                          <Icon
                            size={22}
                            className="text-teal-600 group-hover:text-white transition"
                          />

                        </div>

                        <ChevronRight
                          size={18}
                          className="text-slate-300 group-hover:text-teal-500 group-hover:translate-x-1 transition"
                        />

                      </div>

                      <h3 className="font-sora text-lg font-semibold mt-6">
                        {item.title}
                      </h3>

                      <p className="text-slate-500 text-sm leading-6 mt-3">
                        {item.description}
                      </p>

                      <div className="text-xs text-slate-400 mt-5">
                        {item.articles} articles
                      </div>

                    </motion.button>
                  );

                })}

              </div>

            ) : (

              <div className="text-center py-16">

                <HelpCircle
                  size={40}
                  className="mx-auto text-slate-300"
                />

                <h3 className="font-sora text-xl font-semibold mt-5">
                  No matching categories
                </h3>

                <p className="text-slate-500 text-sm mt-2">
                  Try a different search term.
                </p>

              </div>

            )}

          </div>

        </section>

        {/* =====================================================
            POPULAR ARTICLES
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Knowledge Base
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  Popular
                  <span className="text-teal-500">
                    {" "}articles.
                  </span>

                </h2>

              </div>

              <button className="inline-flex items-center gap-2 text-sm font-semibold text-teal-600">

                View all articles

                <ArrowRight size={15} />

              </button>

            </div>

            {filteredArticles.length > 0 ? (

              <div className="grid md:grid-cols-2 gap-4 mt-12">

                {filteredArticles.map((article, index) => (

                  <motion.button
                    key={article.title}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="group text-left rounded-2xl bg-white border border-slate-100 p-6 hover:border-teal-100 hover:shadow-lg transition"
                  >

                    <div className="flex gap-4">

                      <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center shrink-0">

                        <FileText
                          size={18}
                          className="text-teal-600"
                        />

                      </div>

                      <div className="flex-1">

                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-teal-600">
                          {article.category}
                        </span>

                        <h3 className="font-sora text-base font-semibold mt-2 group-hover:text-teal-600 transition">
                          {article.title}
                        </h3>

                        <p className="text-slate-500 text-sm leading-6 mt-2">
                          {article.description}
                        </p>

                      </div>

                      <ArrowRight
                        size={17}
                        className="text-slate-300 group-hover:text-teal-500 group-hover:translate-x-1 transition shrink-0 mt-1"
                      />

                    </div>

                  </motion.button>

                ))}

              </div>

            ) : (

              <div className="text-center py-16">

                <Search
                  size={36}
                  className="mx-auto text-slate-300"
                />

                <h3 className="font-sora text-xl font-semibold mt-5">
                  No articles found
                </h3>

                <p className="text-slate-500 text-sm mt-2">
                  Try searching with different keywords.
                </p>

              </div>

            )}

          </div>

        </section>

        {/* =====================================================
            QUICK HELP
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Quick Help
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Need something
                <span className="text-teal-500">
                  {" "}else?
                </span>

              </h2>

            </div>

            <div className="grid md:grid-cols-3 gap-5 mt-12">

              {quickLinks.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.button
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -6 }}
                    className="group text-left rounded-3xl border border-slate-100 bg-white p-7 hover:border-teal-100 hover:shadow-xl transition"
                  >

                    <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">

                      <Icon
                        size={22}
                        className="text-teal-600 group-hover:text-white transition"
                      />

                    </div>

                    <h3 className="font-sora text-xl font-semibold mt-6">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-7 mt-3">
                      {item.description}
                    </p>

                    <span className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold mt-6">

                      Get help

                      <ArrowRight size={15} />

                    </span>

                  </motion.button>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            FOR PROFESSIONALS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Healthcare Professionals
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Support for your
                <span className="block text-teal-400">
                  healthcare workflow.
                </span>

              </h2>

              <p className="text-slate-400 leading-8 mt-6">
                Find information about managing your Doxzon professional
                experience, appointments, patient workflows and connected
                healthcare tools.
              </p>

              <button className="inline-flex items-center gap-2 rounded-xl bg-white text-slate-950 px-6 h-12 text-sm font-semibold mt-8 hover:bg-teal-50 transition">

                Professional Help

                <ArrowRight size={16} />

              </button>

            </div>

            <div className="grid grid-cols-2 gap-3">

              {[
                [Stethoscope, "Professional Account"],
                [CalendarCheck, "Scheduling"],
                [Video, "Virtual Care"],
                [FileText, "Digital Records"],
              ].map(([Icon, title]) => (

                <div
                  key={title}
                  className="rounded-2xl bg-white/[0.05] border border-white/10 p-5"
                >

                  <div className="w-10 h-10 rounded-xl bg-teal-500/10 grid place-items-center">

                    <Icon
                      size={18}
                      className="text-teal-400"
                    />

                  </div>

                  <p className="text-sm font-semibold mt-4">
                    {title}
                  </p>

                  <span className="inline-flex items-center gap-1 text-xs text-slate-500 mt-3">

                    Learn more

                    <ArrowRight size={12} />

                  </span>

                </div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            CONTACT SUPPORT
        ====================================================== */}
        <section className="px-6 py-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-[#f4fbfc] border border-teal-50 px-8 py-16 md:px-16">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-200/30 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-200/30 blur-3xl" />

            <div className="relative grid lg:grid-cols-2 gap-10 items-center">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Still Need Help?
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  We're here to
                  <span className="block text-teal-500">
                    help you.
                  </span>

                </h2>

                <p className="text-slate-600 leading-7 mt-5 max-w-xl">
                  If you can't find the answer you're looking for, reach out
                  to the Doxzon support team.
                </p>

              </div>

              <div className="flex flex-col sm:flex-row lg:justify-end gap-3">

                <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-12 text-sm font-semibold hover:bg-teal-500 transition">

                  <Headphones size={17} />

                  Contact Support

                </button>

                <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-slate-200 text-slate-800 px-6 h-12 text-sm font-semibold hover:border-teal-200 transition">

                  <Mail size={17} />

                  Email Us

                </button>

              </div>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default HelpCenter;